import api, { authService } from './api';

const pricingRunFromResponse = (data = {}) => ({
  message: data.message,
  processedCount: data.processed_count || 0,
  pricedCount: data.priced_count || 0,
  skippedCount: data.skipped_count || 0,
  manualResolutionCount: data.manual_resolution_count || 0,
  alreadyPricedCount: data.already_priced_count || 0,
  toBuyCount: data.to_buy_count || 0,
  voidRequiredCount: data.void_required_count || 0,
  unitsClaimed: data.units_claimed || 0,
  items: data.items || [],
  runId: data.run_id || null,
  runStatus: data.run_status || null,
  initiatedByEmail: data.initiated_by_email || null,
  startedAt: data.started_at || null,
  completedAt: data.completed_at || null,
  undoneAt: data.undone_at || null,
  undoneByEmail: data.undone_by_email || null,
  canUndo: Boolean(data.can_undo),
});

const apiErrorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => item?.msg || item?.message)
      .filter(Boolean);
    if (messages.length) return messages.join(' ');
  }
  if (detail?.message) {
    const conflicts = Array.isArray(detail.conflicts) ? ` ${detail.conflicts.join(' ')}` : '';
    return `${detail.message}${conflicts}`;
  }
  if (typeof error.response?.data === 'string' && error.response.data.trim()) {
    return `${fallback}: ${error.response.data.trim()}`;
  }
  if (error.response?.status) return `${fallback} (HTTP ${error.response.status}).`;
  if (error.message) return `${fallback}: ${error.message}`;
  return `${fallback}.`;
};

export const transactionsService = {
  listOrders: async ({
    q,
    showCancelled = false,
    year,
    dateFrom,
    dateTo,
    eventDateFrom,
    eventDateTo,
    department,
    limit = 1000,
  } = {}) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get('/transactions/orders', {
        params: {
          q,
          show_cancelled: showCancelled,
          year,
          date_from: dateFrom,
          date_to: dateTo,
          event_date_from: eventDateFrom,
          event_date_to: eventDateTo,
          department,
          limit,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        items: response.data.items || [],
        totalCount: response.data.total_count || 0,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to load orders',
      };
    }
  },

  updateOrder: async (orderId, payload) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.patch(`/transactions/orders/${encodeURIComponent(orderId)}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        message: response.data.message,
        item: response.data.item,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update order',
      };
    }
  },

  completeOrder: async (orderId, proof) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      let requestBody = null;
      const proofs = Array.isArray(proof) ? proof : (proof ? [proof] : []);
      if (proofs.length) {
        requestBody = new FormData();
        proofs.forEach((file) => requestBody.append('proof', file));
      }
      const response = await api.post(
        `/transactions/orders/${encodeURIComponent(orderId)}/complete`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return {
        success: true,
        message: response.data.message,
        item: response.data.item,
        proofId: response.data.proof_id,
        proofIds: response.data.proof_ids || [],
        transferredQuantity: response.data.transferred_quantity || 0,
        transferIds: response.data.transfer_ids || [],
      };
    } catch (error) {
      return {
        success: false,
        error: apiErrorMessage(error, 'Failed to complete order'),
      };
    }
  },

  getOrderCompletionProofs: async (orderId) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');
      const response = await api.get(
        `/transactions/orders/${encodeURIComponent(orderId)}/completion-proofs`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return { success: true, proofs: response.data.proofs };
    } catch (error) {
      return { success: false, error: apiErrorMessage(error, 'Failed to load order completion proofs') };
    }
  },

  getOrderCompletionProof: async (orderId, proofId) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get(
        proofId
          ? `/transactions/orders/${encodeURIComponent(orderId)}/completion-proofs/${encodeURIComponent(proofId)}`
          : `/transactions/orders/${encodeURIComponent(orderId)}/completion-proof`,
        {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return {
        success: true,
        proof: response.data,
        contentType: response.headers?.['content-type'] || response.data?.type || '',
      };
    } catch (error) {
      const notFound = error.response?.status === 404;
      return {
        success: false,
        notFound,
        error: notFound
          ? 'No proof was uploaded for this order.'
          : apiErrorMessage(error, 'Failed to load order completion proof'),
      };
    }
  },

  createOrder: async (payload) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post('/transactions/orders', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        message: response.data.message,
        item: response.data.item,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to create order',
      };
    }
  },

  listOrderOptions: async () => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get('/transactions/orders/options', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        options: {
          salePoints: response.data.sale_points || [],
          vendors: response.data.vendors || [],
          platforms: response.data.platforms || [],
          statuses: response.data.statuses || [],
          venues: response.data.venues || [],
          eventDates: response.data.event_dates || [],
          ticketTypes: response.data.ticket_types || [],
          operators: response.data.operators || [],
          users: response.data.users || [],
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to load order options',
      };
    }
  },

  listPurchases: async ({ q, showCancelled = false, year, dateFrom, dateTo, limit = 1000 } = {}) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get('/transactions/purchases', {
        params: { q, show_cancelled: showCancelled, year, date_from: dateFrom, date_to: dateTo, limit },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        items: response.data.items || [],
        totalCount: response.data.total_count || 0,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to load purchases',
      };
    }
  },

  updatePurchase: async (purchaseId, payload) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.patch(`/transactions/purchases/${encodeURIComponent(purchaseId)}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        message: response.data.message,
        item: response.data.item,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update purchase',
      };
    }
  },

  listPurchaseOptions: async () => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get('/transactions/purchases/options', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        options: {
          platforms: response.data.platforms || [],
          statuses: response.data.statuses || [],
          venues: response.data.venues || [],
          eventDates: response.data.event_dates || [],
          ticketTypes: response.data.ticket_types || [],
          cards: response.data.cards || [],
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to load purchase options',
      };
    }
  },

  updatePurchaseStatus: async (purchaseId, { status }) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.patch(`/transactions/purchases/${encodeURIComponent(purchaseId)}/status`, {
        status,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        message: response.data.message,
        item: response.data.item,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update purchase status',
      };
    }
  },

  pricePurchases: async ({ dryRun = false } = {}) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post('/transactions/purchases/price', null, {
        params: { dry_run: dryRun },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        message: response.data.message,
        processedCount: response.data.processed_count || 0,
        pricedCount: response.data.priced_count || 0,
        skippedCount: response.data.skipped_count || 0,
        createdEvents: response.data.created_events || 0,
        attachedEvents: response.data.attached_events || 0,
        unitsCreated: response.data.units_created || 0,
        items: response.data.items || [],
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to price purchases',
      };
    }
  },

  priceOrders: async ({ dryRun = false, approvedVoidOrderIds = [], limit } = {}) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post('/transactions/orders/price', {
        approved_void_order_ids: approvedVoidOrderIds,
        limit,
      }, {
        params: { dry_run: dryRun },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        ...pricingRunFromResponse(response.data),
      };
    } catch (error) {
      return {
        success: false,
        error: apiErrorMessage(error, 'Failed to price orders'),
      };
    }
  },

  getOrderPricingHistory: async (offset = 0) => {
    try {
      const response = await api.get('/transactions/orders/pricing-history', {
        params: { offset, limit: 25 },
        headers: { Authorization: `Bearer ${authService.getToken()}` },
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: apiErrorMessage(error, 'Failed to load pricing history') };
    }
  },

  getOrderPricingLog: async (runId, offset = 0) => {
    try {
      const response = await api.get(`/transactions/orders/pricing-history/${runId}`, {
        params: { offset, limit: 100 },
        headers: { Authorization: `Bearer ${authService.getToken()}` },
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: apiErrorMessage(error, 'Failed to load pricing logs') };
    }
  },

  getLatestOrderPricingRun: async () => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get('/transactions/orders/pricing-runs/latest', {
        headers: { Authorization: `Bearer ${token}` },
      });

      return {
        success: true,
        run: response.data ? pricingRunFromResponse(response.data) : null,
      };
    } catch (error) {
      return {
        success: false,
        error: apiErrorMessage(error, 'Failed to load the last order pricing run'),
      };
    }
  },

  undoLatestOrderPricingRun: async () => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post('/transactions/orders/pricing-runs/latest/undo', null, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return {
        success: true,
        ...pricingRunFromResponse(response.data),
      };
    } catch (error) {
      return {
        success: false,
        error: apiErrorMessage(error, 'Failed to undo the last order pricing run'),
      };
    }
  },
};
